import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Ban, Upload, Plus, Search, Trash2, FileSpreadsheet,
  Loader2, Download, AlertTriangle, CheckCircle, X,
  MapPin, Calendar, User, Info, RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDoNotDeliver = () => {
  const { getAuthHeader } = useAuth();
  
  // State
  const [addresses, setAddresses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(50);
  
  // Form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAddress, setNewAddress] = useState('');
  const [newReason, setNewReason] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [adding, setAdding] = useState(false);
  
  // CSV upload state
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Fetch addresses
  const fetchAddresses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/admin/do-not-deliver`,
        {
          ...getAuthHeader(),
          params: {
            skip: currentPage * pageSize,
            limit: pageSize,
            search: searchQuery || undefined
          }
        }
      );
      setAddresses(response.data.addresses);
      setTotalCount(response.data.total);
    } catch (error) {
      console.error('Error fetching addresses:', error);
      toast.error('Failed to load do-not-deliver list');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, currentPage, pageSize, searchQuery]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/api/admin/do-not-deliver/stats`,
        getAuthHeader()
      );
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchAddresses();
    fetchStats();
  }, [fetchAddresses, fetchStats]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(0);
      fetchAddresses();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Add single address
  const handleAddAddress = async (e) => {
    e.preventDefault();
    if (!newAddress.trim()) {
      toast.error('Please enter an address');
      return;
    }

    setAdding(true);
    try {
      await axios.post(
        `${API}/api/admin/do-not-deliver`,
        {
          address: newAddress.trim(),
          reason: newReason.trim() || 'Manual Entry',
          notes: newNotes.trim() || null
        },
        getAuthHeader()
      );
      
      toast.success('Address added to do-not-deliver list');
      setNewAddress('');
      setNewReason('');
      setNewNotes('');
      setShowAddForm(false);
      fetchAddresses();
      fetchStats();
    } catch (error) {
      console.error('Error adding address:', error);
      toast.error(error.response?.data?.detail || 'Failed to add address');
    } finally {
      setAdding(false);
    }
  };

  // Handle CSV upload
  const handleFileUpload = async (file) => {
    if (!file || !file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/api/admin/do-not-deliver/import`,
        formData,
        {
          ...getAuthHeader(),
          headers: {
            ...getAuthHeader().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success(response.data.message);
      fetchAddresses();
      fetchStats();
    } catch (error) {
      console.error('Error uploading CSV:', error);
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  // Delete address
  const handleDelete = async (addressId) => {
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/api/admin/do-not-deliver/${addressId}`,
        getAuthHeader()
      );
      
      toast.success('Address removed from list');
      setDeleteConfirm(null);
      fetchAddresses();
      fetchStats();
    } catch (error) {
      console.error('Error deleting address:', error);
      toast.error('Failed to remove address');
    } finally {
      setDeleting(false);
    }
  };

  // Download template
  const downloadTemplate = () => {
    const csv = 'address,reason\n"123 Main St, Houston, TX 77001","Customer Request"\n"456 Oak Ave, Houston, TX 77002","No Soliciting Sign"';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'do_not_deliver_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="p-6" data-testid="do-not-deliver-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
            <Ban className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Do Not Deliver</h1>
            <p className="text-sm text-gray-500">Manage addresses that should not receive deliveries</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => { fetchAddresses(); fetchStats(); }}
            className="gap-2"
          >
            <RefreshCw size={16} />
            Refresh
          </Button>
          <Button
            onClick={() => setShowAddForm(true)}
            className="bg-red-600 hover:bg-red-700 text-white gap-2"
            data-testid="add-address-btn"
          >
            <Plus size={18} />
            Add Address
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
                <Ban className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-red-600 font-medium">Total Blocked</p>
                <p className="text-2xl font-bold text-red-700">{stats.total.toLocaleString()}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-amber-600 font-medium">Added (7 days)</p>
                <p className="text-2xl font-bold text-amber-700">{stats.recent_7_days}</p>
              </div>
            </div>
          </Card>
          
          {stats.by_reason.slice(0, 2).map((reason, idx) => (
            <Card key={idx} className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-500 rounded-lg flex items-center justify-center">
                  <Info className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-medium truncate max-w-[120px]">{reason.reason}</p>
                  <p className="text-2xl font-bold text-gray-700">{reason.count}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* CSV Upload Section */}
      <Card className="p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
          Import from CSV
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Drag & Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
              dragActive 
                ? 'border-emerald-500 bg-emerald-50' 
                : 'border-gray-300 hover:border-gray-400'
            } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-10 h-10 text-emerald-600 animate-spin" />
                <p className="text-sm text-gray-600">Importing addresses...</p>
              </div>
            ) : (
              <>
                <Upload className={`w-10 h-10 mx-auto mb-3 ${dragActive ? 'text-emerald-600' : 'text-gray-400'}`} />
                <p className="text-sm font-medium text-gray-700 mb-1">
                  Drag & drop your CSV file here
                </p>
                <p className="text-xs text-gray-500 mb-3">or</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
                  data-testid="csv-file-input"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-2"
                >
                  <Upload size={16} />
                  Browse Files
                </Button>
              </>
            )}
          </div>
          
          {/* Instructions */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
              <Info size={16} className="text-blue-600" />
              CSV Format Instructions
            </h4>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span>First column should contain the full address</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span>Second column (optional) can contain the reason</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span>Header row is automatically detected and skipped</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span>Duplicate addresses will be skipped</span>
              </li>
            </ul>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadTemplate}
              className="mt-4 gap-2 text-blue-600 border-blue-300 hover:bg-blue-50"
            >
              <Download size={14} />
              Download Template
            </Button>
          </div>
        </div>
      </Card>

      {/* Address List */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <MapPin className="w-5 h-5 text-red-600" />
            Blocked Addresses
            <Badge variant="outline" className="ml-2">{totalCount}</Badge>
          </h3>
          
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search addresses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : addresses.length === 0 ? (
          <div className="text-center py-12">
            <Ban className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {searchQuery ? 'No addresses match your search' : 'No blocked addresses yet'}
            </p>
            {!searchQuery && (
              <p className="text-sm text-gray-400 mt-1">
                Add addresses manually or import from CSV
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Address</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Reason</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Added</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">By</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {addresses.map((addr) => (
                    <tr key={addr.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Ban className="w-4 h-4 text-red-500 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900">{addr.address}</span>
                        </div>
                        {addr.notes && (
                          <p className="text-xs text-gray-500 mt-0.5 ml-6">{addr.notes}</p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className="text-xs">
                          {addr.reason || 'Unknown'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {addr.created_at ? new Date(addr.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {addr.created_by_name || '—'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(addr)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-${addr.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-gray-500">
                  Showing {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalCount)} of {totalCount}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Add Address Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Plus className="w-5 h-5 text-red-600" />
                Add Address to Block List
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAddForm(false)}
              >
                <X size={18} />
              </Button>
            </div>
            
            <form onSubmit={handleAddAddress} className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Address *</Label>
                <Input
                  placeholder="123 Main St, Houston, TX 77001"
                  value={newAddress}
                  onChange={(e) => setNewAddress(e.target.value)}
                  required
                  data-testid="new-address-input"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium">Reason</Label>
                <Input
                  placeholder="e.g., Customer Request, No Soliciting"
                  value={newReason}
                  onChange={(e) => setNewReason(e.target.value)}
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium">Notes (optional)</Label>
                <textarea
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  rows={2}
                  placeholder="Additional notes..."
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddForm(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={adding || !newAddress.trim()}
                  className="bg-red-600 hover:bg-red-700 text-white gap-2"
                  data-testid="submit-address-btn"
                >
                  {adding ? (
                    <><Loader2 size={16} className="animate-spin" /> Adding...</>
                  ) : (
                    <><Plus size={16} /> Add to Block List</>
                  )}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold">Remove Address?</h3>
            </div>
            
            <p className="text-sm text-gray-600 mb-2">
              Are you sure you want to remove this address from the do-not-deliver list?
            </p>
            <p className="text-sm font-medium text-gray-900 bg-gray-100 p-2 rounded mb-4">
              {deleteConfirm.address}
            </p>
            <p className="text-xs text-amber-600 mb-4">
              This address will be eligible for deliveries again.
            </p>
            
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDelete(deleteConfirm.id)}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white gap-2"
              >
                {deleting ? (
                  <><Loader2 size={16} className="animate-spin" /> Removing...</>
                ) : (
                  <><Trash2 size={16} /> Remove</>
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDoNotDeliver;
